/**
 * @imagine-readonly
 */

import { createFileRoute, useNavigate, useRouter, useSearch } from '@tanstack/react-router'
import { z } from 'zod'
import { useServerFn } from '@tanstack/react-start'
import { useEffect, useRef, useState } from 'react'
import { checkAuthStatusFn, setAppwriteJwtCookieFn } from '@/server/functions/auth'
import { APP_NAME } from '@/lib/constants'

const searchSchema = z.object({
  userId: z.string().optional(),
  secret: z.string().optional(),
  redirect: z.string().optional(),
  error: z.string().optional(),
  error_description: z.string().optional(),
})

export const Route = createFileRoute('/_auth/callback')({
  component: AuthCallbackPage,
  validateSearch: searchSchema,
})

function AuthCallbackPage() {
  const search = useSearch({ from: '/_auth/callback' })
  const navigate = useNavigate()
  const router = useRouter()
  const checkAuthStatus = useServerFn(checkAuthStatusFn)
  const setJwtCookie = useServerFn(setAppwriteJwtCookieFn)
  const [status, setStatus] = useState('Signing you in...')
  const hasCompletedRef = useRef(false)
  const [tokenOverride, setTokenOverride] = useState<{
    userId?: string
    secret?: string
  } | null>(null)

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (search.userId && search.secret) return

    if (window.location.hash) {
      const params = new URLSearchParams(window.location.hash.replace(/^#/, ''))
      const userId = params.get('userId') || undefined
      const secret = params.get('secret') || undefined
      if (userId || secret) {
        setTokenOverride({ userId, secret })
      }
    }
  }, [search.secret, search.userId])

  useEffect(() => {
    const userId = search.userId || tokenOverride?.userId
    const secret = search.secret || tokenOverride?.secret

    if (search.error) {
      setStatus(search.error_description || 'Authentication failed.')
      return
    }

    if (!userId || !secret) {
      // OAuth flow doesn't include userId/secret. Try to create a JWT using the Appwrite session.
      const endpoint = import.meta.env.VITE_APPWRITE_ENDPOINT
      const projectId = import.meta.env.VITE_APPWRITE_PROJECT_ID

      if (!endpoint || !projectId) {
        setStatus('Authentication failed. Please try again.')
        return
      }

      if (hasCompletedRef.current) {
        return
      }
      hasCompletedRef.current = true

      fetch(`${endpoint}/account/jwt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Appwrite-Project': projectId,
        },
        credentials: 'include',
      })
        .then(async (response) => {
          const data = (await response.json().catch(() => ({}))) as {
            jwt?: string
            expire?: string
            message?: string
          }

          if (!response.ok || !data.jwt) {
            throw new Error(
              data.message || 'Unable to create a session token from OAuth.',
            )
          }

          await setJwtCookie({
            data: {
              jwt: data.jwt,
              expires: data.expire,
            },
          })

          await router.invalidate()
          const authStatus = await checkAuthStatus()
          if (!authStatus.email) {
            setStatus('Authentication failed. Please try again.')
            return
          }

          await navigate({ to: search.redirect || '/' })
        })
        .catch(() => {
          setStatus('Authentication failed. Please try again.')
        })

      return
    }

    if (hasCompletedRef.current) {
      return
    }
    hasCompletedRef.current = true

    const endpoint = import.meta.env.VITE_APPWRITE_ENDPOINT
    const projectId = import.meta.env.VITE_APPWRITE_PROJECT_ID

    if (!endpoint || !projectId) {
      setStatus('Authentication failed. Please try again.')
      return
    }

    fetch(`${endpoint}/account/sessions/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Appwrite-Project': projectId,
      },
      credentials: 'include',
      body: JSON.stringify({
        userId,
        secret,
      }),
    })
      .then(async (response) => {
        const data = (await response.json().catch(() => ({}))) as {
          message?: string
        }

        if (!response.ok) {
          const message =
            data.message || 'Unable to create a session from the magic link.'
          if (message.toLowerCase().includes('session is active')) {
            return { sessionAlreadyActive: true }
          }
          throw new Error(message)
        }

        return { sessionAlreadyActive: false }
      })
      .then(async () => {
        const jwtResponse = await fetch(`${endpoint}/account/jwt`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Appwrite-Project': projectId,
          },
          credentials: 'include',
        })

        const jwtData = (await jwtResponse.json().catch(() => ({}))) as {
          jwt?: string
          expire?: string
          message?: string
        }

        if (!jwtResponse.ok || !jwtData.jwt) {
          throw new Error(
            jwtData.message || 'Unable to create a session token from magic link.',
          )
        }

        await setJwtCookie({
          data: {
            jwt: jwtData.jwt,
            expires: jwtData.expire,
          },
        })

        await router.invalidate()
        const authStatus = await checkAuthStatus()

        if (!authStatus.email) {
          setStatus('Authentication failed. Please try again.')
          return
        }

        await navigate({ to: search.redirect || '/' })
      })
      .catch(() => {
        setStatus('Authentication failed. Please try again.')
      })
  }, [checkAuthStatus, navigate, router, search, setJwtCookie, tokenOverride])

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="max-w-md w-full rounded-2xl border bg-white p-6 text-center shadow-sm">
        <h1 className="text-xl font-semibold text-slate-900">
          {APP_NAME}
        </h1>
        <p className="mt-2 text-sm text-slate-600">{status}</p>
      </div>
    </div>
  )
}
